import { ref, type Ref } from 'vue';
import { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
import type { AiDraftFeedbackSubmission } from '@/components/aiDraft/feedback/types.ts';

export interface AiDraftFeedbackAdapter {
    submitFeedback(payload: {
        interactionId: number;
        feedback: DraftFeedback;
        reasons: AiDraftFeedbackSubmission['reasons'];
        details: AiDraftFeedbackSubmission['details'];
    }): Promise<void>;
}

/**
 * Shared AI-draft feedback delivery for the email and SMS composers.
 * The parent composable owns the `feedback` ref (keeps its public API
 * unchanged) and injects it; this owns only the delivery bookkeeping.
 */
export const useAiDraftFeedback = (
    adapter: AiDraftFeedbackAdapter,
    feedback: Ref<DraftFeedback | null>
) => {
    const feedbackInteractionId = ref<number | null>(null);
    const sentFeedback = ref<DraftFeedback | null>(null);
    const pendingFeedback = ref<DraftFeedback | null>(null);

    const clearFeedbackTarget = () => {
        feedbackInteractionId.value = null;
        sentFeedback.value = null;
        pendingFeedback.value = null;
    };

    const isFeedbackTargetCurrent = (interactionId: number) =>
        feedbackInteractionId.value === interactionId;

    const setFeedbackTarget = (interactionId: number) => {
        feedbackInteractionId.value = interactionId > 0 ? interactionId : null;
        sentFeedback.value = null;
    };

    const sendFeedback = async (
        nextFeedback: DraftFeedback,
        submission: AiDraftFeedbackSubmission
    ) => {
        const interactionId = feedbackInteractionId.value;

        if (interactionId === null) {
            return;
        }

        if (sentFeedback.value === nextFeedback || pendingFeedback.value !== null) {
            return;
        }

        pendingFeedback.value = nextFeedback;

        try {
            await adapter.submitFeedback({
                interactionId,
                feedback: nextFeedback,
                reasons: submission.reasons,
                details: submission.details,
            });

            if (isFeedbackTargetCurrent(interactionId)) {
                sentFeedback.value = nextFeedback;
            }
        } catch {
            // Feedback is fire-and-forget: a delivery failure must never block the composer.
        } finally {
            if (isFeedbackTargetCurrent(interactionId) && pendingFeedback.value === nextFeedback) {
                pendingFeedback.value = null;
            }
        }
    };

    const selectFeedback = async (nextFeedback: Exclude<DraftFeedback | null, null>) => {
        feedback.value = nextFeedback;

        if (nextFeedback === DraftFeedback.ThumbsUp) {
            await sendFeedback(DraftFeedback.ThumbsUp, { reasons: [], details: null });
        }
    };

    const submitFeedback = async (submission: AiDraftFeedbackSubmission) => {
        if (feedback.value !== DraftFeedback.ThumbsDown) {
            return;
        }

        await sendFeedback(DraftFeedback.ThumbsDown, submission);
    };

    return {
        feedbackInteractionId,
        sentFeedback,
        pendingFeedback,
        clearFeedbackTarget,
        isFeedbackTargetCurrent,
        setFeedbackTarget,
        sendFeedback,
        selectFeedback,
        submitFeedback,
    };
};
